import React, { useCallback, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCartContext } from 'context/CartContext';
import { formatCurrency } from 'utils/formatters';
import { ROUTES } from 'config/routes';
import CartLineItem from './CartLineItem';
import PricesIncludeTaxNote from 'components/catalog/PricesIncludeTaxNote';

interface CartMiniPreviewProps {
  contrast?: boolean;
}

const CLOSE_DELAY_MS = 180;
const MAX_PREVIEW_ITEMS = 4;

const CartMiniPreview: React.FC<CartMiniPreviewProps> = ({ contrast = false }) => {
  const { t } = useTranslation('cart');
  const theme = useTheme();
  const isTouch = useMediaQuery(theme.breakpoints.down('md'));
  const { cart, itemCount } = useCartContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const items = cart?.items ?? [];
  const open = Boolean(anchorEl) && !isTouch;

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isTouch) return;
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  };

  const badgeSx = contrast
    ? {
        '& .MuiBadge-badge': {
          bgcolor: 'common.white',
          color: 'primary.main',
        },
      }
    : undefined;

  const previewItems = items.slice(0, MAX_PREVIEW_ITEMS);
  const remaining = Math.max(0, items.length - MAX_PREVIEW_ITEMS);

  return (
    <>
      <IconButton
        component={RouterLink}
        to={ROUTES.CART}
        aria-label={t('title')}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        color={contrast ? 'inherit' : 'default'}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
      >
        <Badge badgeContent={itemCount} color={contrast ? undefined : 'primary'} sx={badgeSx}>
          <ShoppingCartOutlinedIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        disableScrollLock
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            onMouseEnter: clearCloseTimer,
            onMouseLeave: scheduleClose,
            elevation: 8,
            sx: {
              width: 360,
              maxWidth: 'calc(100vw - 24px)',
              mt: 0.5,
              overflow: 'hidden',
            },
          },
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': { pointerEvents: 'auto' },
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('title')}
            {itemCount > 0 ? ` (${itemCount})` : ''}
          </Typography>
        </Box>

        {items.length === 0 ? (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('empty')}
            </Typography>
            <Button
              component={RouterLink}
              to={ROUTES.CATALOG}
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => setAnchorEl(null)}
            >
              {t('viewCatalog')}
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2, maxHeight: 320, overflowY: 'auto' }}>
              {previewItems.map((item, index) => (
                <React.Fragment key={item.lineId ?? item.productId}>
                  {index > 0 && <Divider />}
                  <CartLineItem item={item} currency={cart?.currency} compact />
                </React.Fragment>
              ))}
              {remaining > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 1 }}>
                  {t('moreItems', { count: remaining })}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box sx={{ px: 2, py: 1.75 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  mb: 1.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('total')}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {formatCurrency(cart?.total ?? 0, cart?.currency)}
                </Typography>
              </Box>
              <PricesIncludeTaxNote show={cart?.pricesIncludeTax} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  component={RouterLink}
                  to={ROUTES.CART}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => setAnchorEl(null)}
                >
                  {t('viewCart')}
                </Button>
                <Button
                  component={RouterLink}
                  to={ROUTES.CHECKOUT}
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => setAnchorEl(null)}
                >
                  {t('checkout')}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default CartMiniPreview;
